import type { EntityAdapter, EntityState } from '@ngrx/entity';
import { createEntityAdapter } from '@ngrx/entity';
import type { NormalizedColumnEntity } from '@web/types';
import { on } from '@ngrx/store';
import { createImmerReducer } from 'ngrx-immer/store';

import TabsActions from '../tabs/tabs.actions';
import GridsActions from '../grids/grids.actions';
import WidgetsLegacyActions, { WidgetsActions } from '../widgets/widgets.actions';
import ColumnsActions from './columns.actions';

export const COLUMNS_FEATURE_KEY = 'columns';

export type ColumnsState = EntityState<NormalizedColumnEntity>;

export const columnsAdapter: EntityAdapter<NormalizedColumnEntity> = createEntityAdapter<NormalizedColumnEntity>();

/**
 * Columns share their row, so every add, move, copy and remove re-splits it
 * evenly. The row owns the choice between twelfths and percentages, which this
 * reducer cannot see — but it does not have to: the unit travels with the width,
 * so an equal share is just the whole expressed in whichever unit is already
 * there.
 *
 * A zero row's column has no width to split — it fills the block — so it is
 * skipped rather than narrowed at every call site.
 */
const redistributeWidths = (state: ColumnsState, sectionId: string | undefined): ColumnsState => {
    const columns = Object.values(state.entities).filter((col) => col?.parentId === sectionId);

    // The entities an adapter operation just wrote are the frozen action
    // payloads, not immer drafts, so the widths are rewritten through the
    // adapter instead of assigned in place.
    const updates = columns.flatMap((col) => {
        if (!col || !('width' in col.props)) {
            return [];
        }

        const whole = col.props.width.unit === '%' ? 100 : 12;

        return [
            {
                id: col.id,
                changes: {
                    props: { ...col.props, width: { ...col.props.width, value: whole / columns.length } },
                } as Partial<NormalizedColumnEntity>,
            },
        ];
    });

    return columnsAdapter.updateMany(updates, state);
};

export const columnsReducer = createImmerReducer<ColumnsState>(
    columnsAdapter.getInitialState(),
    on(ColumnsActions.init, (state: ColumnsState, { columns }) => columnsAdapter.setAll(columns, state)),
    on(ColumnsActions.columnCreated, ColumnsActions.columnAdded, (state: ColumnsState, { column }) =>
        redistributeWidths(columnsAdapter.addOne(column, state), column.parentId)
    ),
    on(ColumnsActions.columnMoved, (state: ColumnsState, { columnId, currentSectionId }) => {
        const column = state.entities[columnId];
        if (!column) {
            return state;
        }

        const oldSectionId = column.parentId;
        // redistributeWidths returns a fresh state, so the parent is reassigned
        // through the adapter too — immer rejects a mutated draft plus a new value.
        const moved = columnsAdapter.updateOne({ id: columnId, changes: { parentId: currentSectionId } }, state);

        return redistributeWidths(redistributeWidths(moved, oldSectionId), currentSectionId);
    }),
    on(ColumnsActions.removeColumn, (state: ColumnsState, { column }) =>
        redistributeWidths(columnsAdapter.removeOne(column.id, state), column.parentId)
    ),
    on(ColumnsActions.columnCopied, (state: ColumnsState, { column }) =>
        redistributeWidths(columnsAdapter.addOne(column, state), column.parentId)
    ),
    on(ColumnsActions.copyManyColumns, ColumnsActions.addManyColumns, (state: ColumnsState, { columns }) =>
        columnsAdapter.addMany(columns, state)
    ),
    on(ColumnsActions.removeManyColumns, (state: ColumnsState, { columnsIds }) =>
        columnsAdapter.removeMany(columnsIds, state)
    ),
    on(ColumnsActions.updateColumnProperties, (state: ColumnsState, { update }) => {
        const column = state.entities[update.id];
        if (!column) {
            return state;
        }

        // v0 keeps everything under one `props` node, so the six-bag merge the
        // legacy shape needed collapses into one.
        column.props = { ...column.props, ...update.changes.props } as typeof column.props;
        return state;
    }),
    on(ColumnsActions.updateColumnsProperties, (state: ColumnsState, { update }) =>
        columnsAdapter.updateMany(update, state)
    ),
    // react on tab state change
    on(TabsActions.added, TabsActions.created, TabsActions.copied, (state: ColumnsState, { entity, order }) => {
        const currentTab = entity;
        if (!currentTab) {
            return state;
        }

        const column = state.entities[currentTab.parentId];
        if (!column) {
            return state;
        }

        const insertIndex = typeof order === 'number' && order >= 0 ? order : column.children.length;
        column.children.splice(insertIndex, 0, { id: currentTab.id, schema: 'tab' });
        return state;
    }),
    on(TabsActions.removed, (state: ColumnsState, { entity }) => {
        const column = state.entities[entity.parentId];
        if (!column) {
            return state;
        }

        column.children = column.children.filter(({ id }) => id !== entity.id);
        return state;
    }),
    // react on grid state change
    on(
        GridsActions.add, // TODO: why add, should be added?
        GridsActions.created,
        GridsActions.copied,
        (state: ColumnsState, { entity, order }) => {
            const parentId = entity.parentId;
            if (!state.entities?.[parentId]) {
                return state;
            }

            const column = state.entities[parentId];
            column.children.splice(order, 0, { id: entity.id, schema: 'grid' });
            return state;
        }
    ),
    on(GridsActions.remove, (state: ColumnsState, { entity }) => {
        const gridId = entity.id;
        const columnId = entity.parentId;
        const column = state.entities[columnId];
        if (column) {
            column.children = column.children.filter(({ id }) => id !== gridId);
            return state;
        }

        // try to find grid in other columns
        const columns = Object.values(state.entities);
        const result = columns.find((col) => col.children.map(({ id }) => id).includes(gridId));
        if (result) {
            result.children = result.children.filter(({ id }) => id !== gridId);
        }

        return state;
    }),
    on(GridsActions.moveWidgetFromGridToColumn, (state: ColumnsState, { widgetId, nextParentId, nextIndex }) => {
        const column = state.entities[nextParentId];
        if (!column) {
            return state;
        }

        column.children.splice(nextIndex, 0, { id: widgetId, schema: 'widget' });

        return state;
    }),
    on(GridsActions.moveWidgetFromColumnToGrid, (state: ColumnsState, { widgetId, prevParentId }) => {
        const column = state.entities[prevParentId];
        if (!column) {
            return state;
        }

        column.children = column.children.filter(({ id }) => id !== widgetId);
        return state;
    }),
    // react on widgets state change
    on(
        WidgetsLegacyActions.widgetCreated,
        WidgetsLegacyActions.copyWidget,
        WidgetsLegacyActions.addWidget,
        (state: ColumnsState, { widget, index }) => {
            const column = state.entities[widget.columnId];
            column.children.splice(index, 0, { id: widget.id, schema: 'widget' });
            return state;
        }
    ),
    // Widgets are placed by their own `parentId`; a grid parent is the grids slice's to handle.
    on(
        WidgetsActions.created,
        WidgetsActions.added,
        WidgetsActions.copied,
        (state: ColumnsState, { widget, index, gridCellIndex }) => {
            const column = state.entities[widget.parentId];
            if (!column || gridCellIndex !== undefined) {
                return state;
            }

            column.children.splice(index, 0, { id: widget.id, schema: 'widget' });
            return state;
        }
    ),
    on(WidgetsActions.removed, (state: ColumnsState, { widget }) => {
        const column = state.entities[widget.parentId];
        if (!column) {
            return state;
        }

        column.children = column.children.filter(({ id }) => id !== widget.id);
        return state;
    }),
    on(WidgetsLegacyActions.removeWidget, (state: ColumnsState, { widget }) => {
        const widgetId = widget.id;
        const columnId = widget.columnId;
        const column = state.entities[columnId];
        column.children = column.children.filter(({ id }) => id !== widgetId);
        return state;
    }),
    on(
        WidgetsLegacyActions.widgetMoved,
        (state: ColumnsState, { prevColumnId, currentColumnId, previousIndex, currentIndex }) => {
            const oldColumn = state.entities[prevColumnId];
            const currentColumn = state.entities[currentColumnId];

            const widget = oldColumn.children.splice(previousIndex, 1)[0];
            currentColumn.children.splice(currentIndex, 0, widget);
            return state;
        }
    )
);
